import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, FileText, Maximize2, Minimize2, Play, Pause, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

// Function to load pptxgenjs from CDN
const loadPptxGenJS = () => {
  return new Promise((resolve, reject) => {
    if (window.PptxGenJS) {
      resolve(window.PptxGenJS);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js';
    script.onload = () => resolve(window.PptxGenJS);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const PresentationPreview = ({ slides, template, title, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);

  const themeColor = template?.color || '#0D9488';

  const nextSlide = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, slides.length]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentSlide, isFullscreen, nextSlide, prevSlide, onClose]);

  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide(prev => {
          if (prev >= slides.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 4000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, slides.length]);

  const exportToPPT = async () => {
    setIsExporting(true);
    try {
      const PptxGenJS = await loadPptxGenJS();
      const pptx = new PptxGenJS();

      pptx.title = title;
      pptx.author = 'TuriaSlides';
      pptx.company = 'TuriaDesign Solutions';

      slides.forEach((slide) => {
        const pptSlide = pptx.addSlide();

        // Add colored top bar
        pptSlide.addShape(pptx.ShapeType.rect, {
          x: 0, y: 0, w: '100%', h: 0.15,
          fill: { color: themeColor.replace('#', '') }
        });

        switch (slide.type) {
          case 'title':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 2, w: 9, h: 1.5,
              fontSize: 44, bold: true, color: '1a1a1a',
              align: 'center'
            });
            pptSlide.addText(slide.subtitle || '', {
              x: 0.5, y: 3.5, w: 9, h: 0.8,
              fontSize: 20, color: '666666',
              align: 'center'
            });
            pptSlide.addText(slide.date || '', {
              x: 0.5, y: 4.5, w: 9, h: 0.5,
              fontSize: 14, color: '999999',
              align: 'center'
            });
            break;

          case 'image':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            if (slide.imageUrl) {
              pptSlide.addImage({ path: slide.imageUrl, x: 1, y: 1.4, w: 8, h: 4 });
            }
            if (slide.caption) {
              pptSlide.addText(slide.caption, {
                x: 1, y: 5.5, w: 8, h: 0.4,
                fontSize: 12, color: '666666', align: 'center'
              });
            }
            break;

          case 'intro':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            pptSlide.addText(slide.content || '', {
              x: 0.5, y: 1.5, w: slide.imageUrl ? 4.5 : 9, h: 1.5,
              fontSize: 16, color: '444444',
              align: 'left'
            });
            if (slide.imageUrl) {
              pptSlide.addImage({ path: slide.imageUrl, x: 5.5, y: 1.5, w: 4, h: 3 });
            }
            if (slide.bulletPoints) {
              slide.bulletPoints.forEach((point, idx) => {
                pptSlide.addText(`• ${point}`, {
                  x: 0.7, y: 3.2 + (idx * 0.5), w: slide.imageUrl ? 4.3 : 8.5, h: 0.5,
                  fontSize: 14, color: '555555'
                });
              });
            }
            break;

          case 'features':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            if (slide.items) {
              slide.items.forEach((item, idx) => {
                const col = idx % 2;
                const row = Math.floor(idx / 2);
                pptSlide.addText(item.title, {
                  x: 0.5 + (col * 4.5), y: 1.5 + (row * 1.8), w: 4, h: 0.5,
                  fontSize: 18, bold: true, color: '1a1a1a'
                });
                pptSlide.addText(item.description, {
                  x: 0.5 + (col * 4.5), y: 2 + (row * 1.8), w: 4, h: 0.6,
                  fontSize: 12, color: '666666'
                });
              });
            }
            break;

          case 'timeline':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            if (slide.phases) {
              slide.phases.forEach((phase, idx) => {
                pptSlide.addShape(pptx.ShapeType.rect, {
                  x: 0.5 + (idx * 2.3), y: 2, w: 2, h: 2,
                  fill: { color: idx === 0 ? themeColor.replace('#', '') : 'E8E8E8' }
                });
                pptSlide.addText(phase.phase, {
                  x: 0.5 + (idx * 2.3), y: 2.2, w: 2, h: 0.4,
                  fontSize: 12, bold: true, color: idx === 0 ? 'FFFFFF' : '333333',
                  align: 'center'
                });
                pptSlide.addText(phase.title, {
                  x: 0.5 + (idx * 2.3), y: 2.7, w: 2, h: 0.5,
                  fontSize: 11, color: idx === 0 ? 'FFFFFF' : '444444',
                  align: 'center'
                });
                pptSlide.addText(phase.duration, {
                  x: 0.5 + (idx * 2.3), y: 3.3, w: 2, h: 0.4,
                  fontSize: 10, color: idx === 0 ? 'DDDDDD' : '888888',
                  align: 'center'
                });
              });
            }
            break;

          case 'pricing':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            if (slide.packages) {
              slide.packages.forEach((pkg, idx) => {
                const isHighlighted = pkg.highlighted;
                pptSlide.addShape(pptx.ShapeType.rect, {
                  x: 0.5 + (idx * 3.1), y: 1.5, w: 2.8, h: 3.5,
                  fill: { color: isHighlighted ? themeColor.replace('#', '') : 'F5F5F5' },
                  line: { color: isHighlighted ? themeColor.replace('#', '') : 'E0E0E0', width: 1 }
                });
                pptSlide.addText(pkg.name, {
                  x: 0.5 + (idx * 3.1), y: 1.7, w: 2.8, h: 0.4,
                  fontSize: 16, bold: true, color: isHighlighted ? 'FFFFFF' : '333333',
                  align: 'center'
                });
                pptSlide.addText(pkg.price, {
                  x: 0.5 + (idx * 3.1), y: 2.2, w: 2.8, h: 0.5,
                  fontSize: 24, bold: true, color: isHighlighted ? 'FFFFFF' : themeColor.replace('#', ''),
                  align: 'center'
                });
                pkg.features.forEach((feature, fIdx) => {
                  pptSlide.addText(`✓ ${feature}`, {
                    x: 0.6 + (idx * 3.1), y: 2.9 + (fIdx * 0.4), w: 2.6, h: 0.35,
                    fontSize: 10, color: isHighlighted ? 'EEEEEE' : '666666'
                  });
                });
              });
            }
            break;

          case 'contact':
            pptSlide.addText(slide.title, {
              x: 0.5, y: 0.5, w: 9, h: 0.8,
              fontSize: 32, bold: true, color: themeColor.replace('#', '')
            });
            pptSlide.addText(slide.content || '', {
              x: 0.5, y: 1.8, w: 9, h: 0.8,
              fontSize: 24, color: '333333',
              align: 'center'
            });
            if (slide.contactInfo) {
              pptSlide.addText(`Email: ${slide.contactInfo.email}`, {
                x: 2, y: 3, w: 6, h: 0.5,
                fontSize: 14, color: '555555',
                align: 'center'
              });
              pptSlide.addText(`Tel: ${slide.contactInfo.phone}`, {
                x: 2, y: 3.5, w: 6, h: 0.5,
                fontSize: 14, color: '555555',
                align: 'center'
              });
              pptSlide.addText(`Web: ${slide.contactInfo.website}`, {
                x: 2, y: 4, w: 6, h: 0.5,
                fontSize: 14, color: themeColor.replace('#', ''),
                align: 'center'
              });
            }
            break;

          default:
            pptSlide.addText(slide.title || 'Slide', {
              x: 0.5, y: 2, w: 9, h: 1,
              fontSize: 32, bold: true, color: '333333',
              align: 'center'
            });
        }
      });

      await pptx.writeFile({ fileName: `${title.replace(/\s+/g, '_')}_TuriaSlides.pptx` });

      toast({
        title: '¡Exportación exitosa!',
        description: 'Tu presentación PPT se ha descargado correctamente.'
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: 'Error al exportar',
        description: 'Hubo un problema al generar el archivo PPT.',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = () => {
    toast({
      title: 'Exportar a PDF',
      description: 'Funcionalidad de exportación a PDF próximamente disponible.'
    });
  };

  const renderSlideContent = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{slide.title}</h1>
            <p className="text-xl text-gray-600 mb-6">{slide.subtitle}</p>
            <p className="text-sm text-gray-400">{slide.date}</p>
          </div>
        );

      case 'image':
        return (
          <div className="flex flex-col items-center justify-center h-full px-8 py-6">
            <h2 className="text-3xl font-bold mb-4" style={{ color: themeColor }}>{slide.title}</h2>
            {slide.imageUrl && (
              <div className="relative w-full h-[60%] rounded-xl overflow-hidden shadow-md mb-4 bg-gray-100 flex items-center justify-center">
                <img
                  src={slide.imageUrl}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=60';
                    e.target.onerror = null;
                  }}
                />
              </div>
            )}
            <p className="text-lg text-gray-600 text-center italic">{slide.caption}</p>
          </div>
        );

      case 'intro':
        return (
          <div className="px-8 py-6 h-full flex flex-col">
            <h2 className="text-3xl font-bold mb-6" style={{ color: themeColor }}>{slide.title}</h2>
            <div className={`flex gap-8 ${slide.imageUrl ? 'flex-row' : 'flex-col'}`}>
              <div className={slide.imageUrl ? 'flex-1' : 'w-full'}>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">{slide.content}</p>
                {slide.bulletPoints && (
                  <ul className="space-y-3">
                    {slide.bulletPoints.map((point, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-gray-600">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: themeColor }} />
                        {point}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {slide.imageUrl && (
                <div className="flex-1 h-64 rounded-xl overflow-hidden shadow-md bg-gray-100">
                  <img
                    src={slide.imageUrl}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&auto=format&fit=crop&q=60';
                      e.target.onerror = null;
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="px-8 py-6">
            <h2 className="text-3xl font-bold mb-8" style={{ color: themeColor }}>{slide.title}</h2>
            <div className="grid grid-cols-2 gap-6">
              {slide.items?.map((item, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="px-8 py-6">
            <h2 className="text-3xl font-bold mb-8" style={{ color: themeColor }}>{slide.title}</h2>
            <div className="flex gap-4">
              {slide.phases?.map((phase, idx) => (
                <div
                  key={idx}
                  className={`flex-1 p-4 rounded-xl text-center ${idx === 0 ? 'text-white' : 'bg-gray-100 text-gray-800'}`}
                  style={idx === 0 ? { backgroundColor: themeColor } : {}}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{phase.phase}</span>
                  <h4 className="font-semibold mt-2">{phase.title}</h4>
                  <p className="text-sm mt-1 opacity-75">{phase.duration}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="px-8 py-6">
            <h2 className="text-3xl font-bold mb-8" style={{ color: themeColor }}>{slide.title}</h2>
            <div className="flex gap-4 justify-center">
              {slide.packages?.map((pkg, idx) => (
                <div
                  key={idx}
                  className={`flex-1 max-w-[200px] p-5 rounded-xl border-2 ${pkg.highlighted
                    ? 'border-transparent text-white shadow-lg scale-105'
                    : 'border-gray-200 bg-white'
                    }`}
                  style={pkg.highlighted ? { backgroundColor: themeColor } : {}}
                >
                  <h3 className="font-semibold text-lg">{pkg.name}</h3>
                  <p className={`text-2xl font-bold my-3 ${pkg.highlighted ? 'text-white' : ''}`} style={!pkg.highlighted ? { color: themeColor } : {}}>
                    {pkg.price}
                  </p>
                  <ul className="space-y-2 text-sm">
                    {pkg.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2">
                        <span>✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <h2 className="text-3xl font-bold mb-4" style={{ color: themeColor }}>{slide.title}</h2>
            <p className="text-xl text-gray-700 mb-8">{slide.content}</p>
            {slide.contactInfo && (
              <div className="space-y-3">
                <p className="text-gray-600">Email: {slide.contactInfo.email}</p>
                <p className="text-gray-600">Tel: {slide.contactInfo.phone}</p>
                <p style={{ color: themeColor }}>Web: {slide.contactInfo.website}</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <h2 className="text-3xl font-bold text-gray-900">{slide.title}</h2>
          </div>
        );
    }
  };

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 z-50 flex flex-col ${isFullscreen ? 'bg-black' : 'bg-black/80'
        }`}
    >
      {/* Top Bar */}
      {!isFullscreen && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-b">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <span className="text-sm text-gray-500">
              Slide {currentSlide + 1} de {slides.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPlaying(!isPlaying)}
              className="flex items-center gap-1"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isPlaying ? 'Pausar' : 'Reproducir'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-1"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToPDF}
              className="flex items-center gap-1"
            >
              <FileText className="w-4 h-4" />
              PDF
            </Button>
            <Button
              size="sm"
              onClick={exportToPPT}
              disabled={isExporting}
              className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 text-white"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {isExporting ? 'Exportando...' : 'Descargar PPT'}
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Slide Container */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div
          className={`bg-white rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ${isFullscreen ? 'w-full h-full max-w-none' : 'w-full max-w-4xl aspect-video'
            }`}
        >
          {/* Slide Color Bar */}
          <div className="h-1.5" style={{ backgroundColor: themeColor }} />

          {/* Slide Content */}
          <div className="h-full">
            {renderSlideContent(slides[currentSlide])}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 py-4 bg-white/10">
        <Button
          variant="ghost"
          size="icon"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="text-white hover:bg-white/20 disabled:opacity-30"
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>

        {/* Slide Indicators */}
        <div className="flex gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide
                ? 'w-8 bg-white'
                : 'bg-white/40 hover:bg-white/60'
                }`}
            />
          ))}
        </div>

        <Button
          variant="ghost"
          size="icon"
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className="text-white hover:bg-white/20 disabled:opacity-30"
        >
          <ChevronRight className="w-6 h-6" />
        </Button>
      </div>

      {/* Fullscreen close hint */}
      {isFullscreen && (
        <button
          onClick={() => setIsFullscreen(false)}
          className="absolute top-4 right-4 p-2 text-white/60 hover:text-white transition-colors"
        >
          <Minimize2 className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default PresentationPreview;
